import { useState, useEffect } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { Mixpanel } from '../../../services/Mixpanel';
import { BookCard } from './BookCard';

const REQUEST_A_BOOK_URL = 'https://forms.gle/HVNp2QBV4hPHKkpT8';

/**
 * Searchable grid view for browsing and selecting a book from the Diffit library.
 * @param {Object} props
 * @param {string} props.searchQuery - Current search input value.
 * @param {Function} props.setSearchQuery - Setter for the search input value.
 * @param {Array} props.booksInSearch - Filtered list of books matching the search query.
 * @param {number} props.diffitBooksLength - Total number of available books (used for loading state).
 * @param {Object} props.inputRef - React ref attached to the search input.
 * @param {Function} props.debouncedMixpanelTrack - Debounced Mixpanel tracking function for search events.
 * @param {Function} props.onSelectBook - Callback invoked with a book object when selected.
 */
export const BrowsingView = ({
  searchQuery,
  setSearchQuery,
  booksInSearch,
  diffitBooksLength,
  inputRef,
  debouncedMixpanelTrack,
  onSelectBook,
}) => {
  // Defer rendering the book grid until after the first paint so the modal shell appears instantly
  const [ready, setReady] = useState(diffitBooksLength > 0);
  useEffect(() => {
    setReady(true);
  }, []);

  return (
    <div className='flex flex-col overflow-hidden'>
      {/* Search Input */}
      <div className='px-4 pt-3 pb-2 flex-shrink-0'>
        <div className='relative'>
          <MagnifyingGlassIcon className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
          <input
            ref={inputRef}
            type='text'
            className='w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-0 focus:border-gray-400 placeholder:text-gray-400'
            placeholder={diffitBooksLength === 0 ? 'Loading...' : 'Search by title or author'}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              debouncedMixpanelTrack('Unlimited Book Modal Searched', {
                query: e.target.value,
              });
            }}
            autoComplete='off'
          />
        </div>
      </div>

      {/* Book Grid */}
      <div className='flex-1 overflow-y-auto px-4 pb-3 scrollbar-minimal'>
        {!ready ? (
          <div className='flex items-center justify-center flex-1'>
            <LoadingSpinner color='text-gray-300' />
          </div>
        ) : booksInSearch.length > 0 ? (
          <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-4'>
            {booksInSearch.map((book) => (
              <BookCard key={book.title + '--' + book.author} book={book} onClick={onSelectBook} />
            ))}
          </div>
        ) : (
          <div className='flex flex-col items-center justify-center py-12 text-gray-500'>
            <p className='text-center'>No books found matching "{searchQuery}"</p>
          </div>
        )}

        {/* Request A Book Link */}
        {ready && (
          <div className='mt-6 pt-4 border-t border-gray-200'>
            <a
              href={REQUEST_A_BOOK_URL}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => Mixpanel.track('Unlimited Book Modal Requested A Book')}
              className='flex items-center justify-center text-emerald-700 hover:text-emerald-800 transition-colors'>
              <span>Don't see your book? Request it</span>
              <ChevronRightIcon className='h-4 w-4 ml-0.5' />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
